import React from 'react';

/**
 * Card Styles
 *
 * Reusable container components for grouping related content with visual separation.
 *
 * Usage Philosophy:
 * - 90% of cases: Use pre-composed cardStyles (cardStyles.default, cardStyles.elevated, etc.)
 * - 10% of cases: Use building blocks for custom combinations (cardBase + cardPadding + cardShadow)
 *
 * @example Pre-composed (recommended)
 * ```tsx
 * <div style={cardStyles.default}>
 *   <h2>Card Title</h2>
 *   <p>Card content...</p>
 * </div>
 * ```
 *
 * @example Building blocks (edge cases)
 * ```tsx
 * <div style={{
 *   ...cardBase,
 *   ...cardPadding.large,
 *   ...cardShadow.elevated
 * }}>
 *   Custom Card
 * </div>
 * ```
 *
 * Card Variants:
 * - Default: Standard card with border and subtle shadow
 * - Elevated: Card with larger shadow for emphasis
 * - Flat: Card with border only, no shadow
 * - Outlined: Card with thicker border and no background
 */

// ============================================================================
// PRE-COMPOSED CARD STYLES (90% usage)
// ============================================================================

export const cardStyles = {
  /**
   * Default Card - Standard card with border and subtle shadow
   * Use for: General content containers, form sections, list items
   */
  default: {
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    padding: '1.5rem',
    boxShadow: 'var(--shadow)',
    marginBottom: '1rem',
  } as React.CSSProperties,

  /**
   * Elevated Card - Card with larger shadow for emphasis
   * Use for: Important content, featured items, modals
   */
  elevated: {
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    padding: '1.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    marginBottom: '1rem',
  } as React.CSSProperties,

  /**
   * Flat Card - Card with border only, no shadow
   * Use for: Subtle content grouping, nested cards
   */
  flat: {
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    padding: '1.5rem',
    marginBottom: '1rem',
  } as React.CSSProperties,

  /**
   * Outlined Card - Card with thicker border and no background
   * Use for: Wireframe appearance, secondary content
   */
  outlined: {
    backgroundColor: 'transparent',
    border: '2px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    padding: '1.5rem',
    marginBottom: '1rem',
  } as React.CSSProperties,

  /**
   * Compact Card - Smaller padding for dense layouts
   * Use for: List items, grid items, compact dashboards
   */
  compact: {
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    padding: '1rem',
    boxShadow: 'var(--shadow)',
    marginBottom: '0.5rem',
  } as React.CSSProperties,

  /**
   * Message Card - Lightweight card for message lists
   * Use for: Inbox, sent, and system announcement cards
   */
  message: {
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-light)',
    borderRadius: 'var(--radius)',
    padding: '0.75rem 1rem',
    boxShadow: 'var(--shadow)',
    marginBottom: 0,
  } as React.CSSProperties,

  /**
   * Spacious Card - Larger padding for breathing room
   * Use for: Form containers, content sections, feature highlights
   */
  spacious: {
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    padding: '2rem',
    boxShadow: 'var(--shadow)',
    marginBottom: '2rem',
  } as React.CSSProperties,
};

// ============================================================================
// BUILDING BLOCKS (10% usage for edge cases)
// ============================================================================

/**
 * Card Base - Common properties for all cards
 * Use with cardPadding, cardShadow, and cardBorder for custom combinations
 */
export const cardBase = {
  backgroundColor: 'var(--surface-color)',
  borderRadius: 'var(--radius)',
} as React.CSSProperties;

/**
 * Card Padding - Predefined padding variants
 */
export const cardPadding = {
  none: {
    padding: '0',
  } as React.CSSProperties,

  compact: {
    padding: '1rem',
  } as React.CSSProperties,

  default: {
    padding: '1.5rem',
  } as React.CSSProperties,

  spacious: {
    padding: '2rem',
  } as React.CSSProperties,

  extraSpacious: {
    padding: '3rem',
  } as React.CSSProperties,
};

/**
 * Card Shadow - Predefined shadow variants
 */
export const cardShadow = {
  none: {
    boxShadow: 'none',
  } as React.CSSProperties,

  subtle: {
    boxShadow: 'var(--shadow)',
  } as React.CSSProperties,

  elevated: {
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  } as React.CSSProperties,

  strong: {
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  } as React.CSSProperties,
};

/**
 * Card Border - Predefined border variants
 */
export const cardBorder = {
  none: {
    border: 'none',
  } as React.CSSProperties,

  subtle: {
    border: '1px solid var(--border-light)',
  } as React.CSSProperties,

  default: {
    border: '1px solid var(--border-color)',
  } as React.CSSProperties,

  thick: {
    border: '2px solid var(--border-color)',
  } as React.CSSProperties,

  primary: {
    border: '2px solid var(--primary-color)',
  } as React.CSSProperties,
};

/**
 * Card Spacing - Predefined margin bottom variants
 */
export const cardSpacing = {
  none: {
    marginBottom: '0',
  } as React.CSSProperties,

  compact: {
    marginBottom: '0.5rem',
  } as React.CSSProperties,

  default: {
    marginBottom: '1rem',
  } as React.CSSProperties,

  spacious: {
    marginBottom: '2rem',
  } as React.CSSProperties,

  extraSpacious: {
    marginBottom: '3rem',
  } as React.CSSProperties,
};
